/**
 * MER-481: live row counts for the admin Canonical Entities table.
 *
 * The Rows column used to render sync_log.rows_upserted from the most
 * recent attempt, so a FAILED attempt read as "0 rows" while the prod
 * table actually held thousands (147 / 9,144 / 119 during the MER-479
 * triage). The column now shows the live table count; these pure
 * helpers map entity keys to tables and format the count honestly.
 *
 * Plain .mjs so the node:test suite imports it directly (the
 * connectorHealth.mjs precedent).
 */

/**
 * sync_log entity keys are UPPERCASE canonical entity names; the
 * Supabase tables are the lowercase forms (SERVICE_EVENT ->
 * service_event). Anything outside the safe identifier charset
 * returns null so a malformed key can never reach a query.
 *
 * @param {string} entityKey
 * @returns {string | null}
 */
export function entityTableFor(entityKey) {
  if (typeof entityKey !== "string" || entityKey.length === 0) return null;
  const table = entityKey.toLowerCase();
  return /^[a-z][a-z0-9_]*$/.test(table) ? table : null;
}

/**
 * Format a live row count for the table cell.
 *
 * Counts come from PostgREST `count: "estimated"`: exact below the
 * server's max-rows threshold, planner estimate above it. Anything
 * above 1,000 is therefore displayed with a tilde so an estimate is
 * never presented as an exact figure; small counts (where exactness
 * matters for "is this table empty?") are exact.
 *
 * @param {number | null | undefined} count
 * @returns {string}   "9,144" | "~95,210" | "-" (unavailable)
 */
export function formatLiveRowCount(count) {
  if (typeof count !== "number" || !Number.isFinite(count) || count < 0) return "-";
  const text = Math.round(count).toLocaleString("en-US");
  return count > 1000 ? `~${text}` : text;
}

/**
 * MER-540: is a `count: "estimated"` read stale relative to the
 * table's most recent sync?
 *
 * `count: "estimated"` answers from planner statistics above the
 * server's max-rows threshold (see formatLiveRowCount above), and
 * those statistics only refresh on ANALYZE (autovacuum or manual).
 * Right after a large sync the count can read far below the true row
 * count with no other signal that anything is wrong (the MER-540
 * live-pass incident: CUSTOMER read ~3,070 vs a true 9,210). This
 * compares the table's last-analyzed timestamp (from the
 * admin_table_stats() RPC, migration 0078) against the entity's last
 * successful sync: statistics older than the sync cannot possibly
 * reflect the rows that sync wrote.
 *
 * A table with NO analyze timestamp yet (small/new tables autovacuum
 * has not reached) has no staleness signal at all -- that is
 * deliberately NOT flagged stale here, since "unanalyzed" and "stale"
 * are different conditions and conflating them would flag every
 * freshly-provisioned customer's tables on day one.
 *
 * @param {string | null | undefined} lastAnalyze
 * @param {string | null | undefined} lastAutoanalyze
 * @param {string | null | undefined} lastSyncAt
 * @returns {boolean}
 */
export function isCountStale(lastAnalyze, lastAutoanalyze, lastSyncAt) {
  if (typeof lastSyncAt !== "string" || lastSyncAt.length === 0) return false;
  const syncTime = Date.parse(lastSyncAt);
  if (Number.isNaN(syncTime)) return false;

  const candidates = [lastAnalyze, lastAutoanalyze]
    .filter((v) => typeof v === "string" && v.length > 0)
    .map((v) => Date.parse(v))
    .filter((t) => !Number.isNaN(t));
  if (candidates.length === 0) return false;

  const statsTime = Math.max(...candidates);
  return statsTime < syncTime;
}
